import { Box, Card, Collapse, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { ExpandMore, ExpandLess, ThumbUp, ThumbDown } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Icons from "../../../assets/Icons";
import { CPTType, ICDType } from "../../codingModule/type/normalizeCodesForUi";
import {
  VoteType,
  getBadgeColor,
  getConfidenceNumber,
  getCurrentLiked,
} from "../helpers/caseCodesHelpers";

const renderVoiceNoteQuote = (quote: string, index: number) => {
  const cleaned = quote.replace(/^[^a-zA-Z0-9]+/, "");
  return (
    <Typography key={`${index}-${cleaned}`} fontSize="13px" mt={0.5}>
      "{cleaned}"
    </Typography>
  );
};

type ThumbButtonProps = {
  type: Exclude<VoteType, null>;
  currentLiked: VoteType;
  onClick: () => void;
};

const ThumbButton = ({ type, currentLiked, onClick }: ThumbButtonProps) => {
  const iconColor = type === currentLiked ? "#A68BD0" : "#C1C1C1";

  return (
    <Tooltip title={`Thumbs ${type}`}>
      <IconButton onClick={onClick}>
        {type === "up" ? (
          <ThumbUp sx={{ color: iconColor }} />
        ) : (
          <ThumbDown sx={{ color: iconColor }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

type IcdCardProps = {
  icd: ICDType;
  cptIndex: number;
  expandedIcdIndex: number | null;
  onToggleIcd: (cptIndex: number) => void;
  forceExpandAll: boolean;
};

const IcdCard = ({
  icd,
  cptIndex,
  expandedIcdIndex,
  onToggleIcd,
  forceExpandAll,
}: IcdCardProps) => (
  <Box
    key={icd?.id}
    flex="1 1 30%"
    p={2}
    borderRadius={3}
    bgcolor="#FFFFFF"
    position="relative"
  >
    <Box display="flex" flexDirection="row" justifyContent="space-between">
      <Box display="flex" gap={2}>
        <Typography fontWeight={700}>ICD-10: {icd?.icd_code}</Typography>
        <Box
          minWidth="66px"
          height="24px"
          borderRadius="4"
          fontSize="13px"
          fontWeight={600}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
          sx={{
            backgroundColor: getBadgeColor(
              getConfidenceNumber(icd?.icd_confidence),
            ),
          }}
        >
          <Icons.Rating width={14} height={14} style={{ marginTop: -1 }} />
          <Typography fontSize="13px" color="#595959">
            {icd.icd_confidence}
          </Typography>
        </Box>
      </Box>
      <Box>
        <IconButton onClick={() => onToggleIcd(cptIndex)}>
          {expandedIcdIndex === cptIndex ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
    </Box>

    <Box>
      <Typography fontSize="14px">{icd.icd_description}</Typography>
    </Box>

    <Collapse in={forceExpandAll || expandedIcdIndex === cptIndex}>
      <Box mt={1}>
        <Divider sx={{ my: 1 }} />
        <Typography fontWeight={700} fontSize="14px">
          Reason
        </Typography>
        <Typography fontSize="13px">{icd.icd_rationale}</Typography>
        {icd.icd_voice_note_quotes?.length > 0 && (
          <Box my={2}>
            <Typography fontWeight={700} fontSize="14px">
              Supporting Voice Notes
            </Typography>
            {icd.icd_voice_note_quotes.map(renderVoiceNoteQuote)}
          </Box>
        )}
      </Box>
    </Collapse>
  </Box>
);

type CodeSectionProps = {
  list: CPTType[];
  sectionType: "CPT" | "HCPCS";
  expandedIndex: number | null;
  onToggle: (index: number) => void;
  expandedIcdIndex: number | null;
  onToggleIcd: (cptIndex: number) => void;
  forceExpandAll: boolean;
  onThumbUp: (cpt: CPTType, codeType: "CPT" | "HCPCS") => void;
  onThumbDown: (cpt: CPTType, codeType: "CPT" | "HCPCS") => void;
};

const CodeSection = ({
  list,
  sectionType,
  expandedIndex,
  onToggle,
  expandedIcdIndex,
  onToggleIcd,
  forceExpandAll,
  onThumbUp,
  onThumbDown,
}: CodeSectionProps) => {
  const { t } = useTranslation();

  const filtered = list.filter(
    (cpt: CPTType) => getConfidenceNumber(cpt.cpt_confidence) >= 25,
  );

  if (filtered.length === 0) return null;

  return (
    <>
      <Typography fontWeight={700} fontSize="1.50rem" my={0.5}>
        {sectionType === "HCPCS"
          ? t("HCPCS_Codes_Heading")
          : t("Codes_Heading")}
      </Typography>
      <Typography fontSize="0.875rem" mb={1.5}>
        {sectionType === "HCPCS"
          ? t("HCPCS_Codes_Subheading")
          : t("Codes_Subheading")}
      </Typography>
      {filtered.map((cpt: CPTType, cptIndex: number) => (
        <Card
          key={cpt?.id}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3.5,
            backgroundColor: "#FFFFFF80",
            boxShadow: "none",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Box display="flex" gap={2}>
                <Typography fontWeight={700}>
                  {" "}
                  {sectionType === "HCPCS" ? "HCPCS" : "CPT"}: {cpt?.cpt_code}
                </Typography>
                <Box
                  minWidth="66px"
                  height="24px"
                  borderRadius="4"
                  fontSize="13px"
                  fontWeight={600}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={0.5}
                  sx={{
                    backgroundColor: getBadgeColor(
                      getConfidenceNumber(cpt?.cpt_confidence),
                    ),
                    color: "#000",
                  }}
                >
                  <Icons.Rating width={14} height={14} style={{ marginTop: -1 }} />
                  <Typography fontSize={"13px"} color={"#595959"}>
                    {cpt?.cpt_confidence}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography>{cpt?.cpt_description}</Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <ThumbButton
                type="up"
                currentLiked={getCurrentLiked(cpt?.liked)}
                onClick={() => onThumbUp(cpt, sectionType)}
              />
              <ThumbButton
                type="down"
                currentLiked={getCurrentLiked(cpt?.liked)}
                onClick={() => onThumbDown(cpt, sectionType)}
              />
              <IconButton onClick={() => onToggle(cptIndex)}>
                {expandedIndex === cptIndex ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>

          <Collapse in={forceExpandAll || expandedIndex === cptIndex}>
            <Box mt={2}>
              <Divider sx={{ my: 1 }} />
              <Typography fontWeight={700}>Reason</Typography>
              <Typography>{cpt?.cpt_rationale}</Typography>
              {cpt?.cpt_voice_note_quotes?.length > 0 && (
                <Box my={2}>
                  <Typography fontWeight={700} mt={1}>
                    Supporting Voice Notes
                  </Typography>
                  {cpt?.cpt_voice_note_quotes?.map(renderVoiceNoteQuote)}
                </Box>
              )}
            </Box>
          </Collapse>

          {sectionType === "CPT" && (
            <Box display="flex" mt={3} gap={2} flexWrap="wrap">
              {cpt?.associated_icds
                ?.filter(
                  (icd: ICDType) => getConfidenceNumber(icd?.icd_confidence) >= 25,
                )
                .map((icd: ICDType) => (
                  <IcdCard
                    key={icd?.id}
                    icd={icd}
                    cptIndex={cptIndex}
                    expandedIcdIndex={expandedIcdIndex}
                    onToggleIcd={onToggleIcd}
                    forceExpandAll={forceExpandAll}
                  />
                ))}
            </Box>
          )}
        </Card>
      ))}
    </>
  );
};

export default CodeSection;
